import InfoTooltip from 'components/InfoTooltip';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { CountryManagementListItem, CountryManagementUpdateDto } from 'types/country-management';
import { t } from 'utils/getTranslations';

interface Props {
    country: CountryManagementListItem;
    onClose: () => void;
    onSave: (countryCode: string, dto: CountryManagementUpdateDto) => Promise<void>;
}

interface FormData {
    isHighRiskJurisdiction: string;
    isStrategicMarket: string;
    ambassadors: { id: number; name: string }[];
}

const YES_NO_OPTIONS = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

const CountryManagementEditModal = ({ country, onClose, onSave }: Props) => {
    const defaultValues: FormData = useMemo(
        () => ({
            isHighRiskJurisdiction: String(country.isHighRiskJurisdiction),
            isStrategicMarket: String(country.isStrategicMarket),
            ambassadors: country.ambassadors.map((a) => ({ id: a.id, name: a.name })),
        }),
        [country]
    );

    const { handleSubmit, control, setValue } = useForm<FormData>({
        defaultValues,
        values: defaultValues,
    });

    const handleOnSave = async () => {
        await handleSubmit(async (data) => {
            const dto: CountryManagementUpdateDto = {
                isHighRiskJurisdiction: data.isHighRiskJurisdiction === 'true',
                isStrategicMarket: data.isStrategicMarket === 'true',
                ambassadorEmployeeIds: data.ambassadors.map((a) => a.id),
            };
            await onSave(country.countryCode, dto);
        })();
    };

    const translationsText = country.translations?.map((tr) => tr.value).join(', ') || '-';

    return (
        <>
            <ModalHeader title={t('country-management.edit-country')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div className="flex flex-col gap-4">
                    <div>
                        <span className="text-caption text-fg-secondary">{t('country-management.country-eng')}</span>
                        <p className="text-body1">
                            {country.nameEng} ({country.countryCode})
                        </p>
                    </div>
                    {translationsText !== '-' && (
                        <div>
                            <span className="text-caption text-fg-secondary">
                                {t('country-management.country-translations')}
                            </span>
                            <p className="text-body1">{translationsText}</p>
                        </div>
                    )}
                    <RadioInput
                        setValue={setValue}
                        control={control}
                        label="country-management.high-risk-jurisdiction"
                        name="isHighRiskJurisdiction"
                        options={YES_NO_OPTIONS}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        row
                    />
                    <DynamicMultiSelectInput
                        setValue={setValue}
                        control={control}
                        label="country-management.market-ambassadors"
                        name="ambassadors"
                        paginated={{
                            fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                            searchField: 'fullName',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="country-management.select-ambassadors"
                        rightLabelComponent={<InfoTooltip text="country-management.tooltips.market-ambassadors" />}
                    />
                    <RadioInput
                        setValue={setValue}
                        control={control}
                        label="country-management.strategic-market"
                        name="isStrategicMarket"
                        options={YES_NO_OPTIONS}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        row
                        rightLabelComponent={<InfoTooltip text="country-management.tooltips.strategic-market" />}
                    />
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void handleOnSave();
                }}
            />
        </>
    );
};

export default CountryManagementEditModal;
